import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Alert, Link, Divider,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';

/**
 * The operator second-factor step, shown after a correct password when the
 * account is an operator (SUPER_ADMIN / WEB_ADMIN).
 *
 *  - mode="enroll"  first time: fetch a secret, show a QR to scan in an
 *                   authenticator app, then confirm with a 6-digit code.
 *  - mode="verify"  already enrolled: just ask for the current 6-digit code.
 *
 * onSuccess(authResponse) is called once the session is established.
 */
const OperatorMfaStep = ({ mode, pendingToken, onSuccess, onCancel }) => {
  const { mfaEnroll, verifyMfaEnroll, verifyMfa } = useAuth();

  const [enroll, setEnroll] = useState(null); // { secret, otpauthUri }
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(mode === 'enroll');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // On enrollment, fetch the secret + otpauth URI for the QR.
  useEffect(() => {
    if (mode !== 'enroll') return undefined;
    let active = true;
    mfaEnroll(pendingToken)
      .then((d) => { if (active) { setEnroll(d); setLoading(false); } })
      .catch((e) => { if (active) { setError(e?.message || 'Could not start setup'); setLoading(false); } });
    return () => { active = false; };
  }, [mode, pendingToken, mfaEnroll]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code from your app'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const resp = mode === 'enroll'
        ? await verifyMfaEnroll(pendingToken, code)
        : await verifyMfa(pendingToken, code);
      onSuccess(resp);
    } catch (err) {
      setError(err?.message || 'Invalid code');
      setSubmitting(false);
    }
  };

  const codeField = (
    <TextField
      fullWidth
      label="6-digit code"
      value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      error={Boolean(error)}
      helperText={error || ' '}
      margin="normal"
      variant="outlined"
      autoFocus
      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { letterSpacing: '0.4em', fontSize: 20, textAlign: 'center' } }}
    />
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
        Two-factor authentication
      </Typography>

      {mode === 'enroll' && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
          Scan this with an authenticator app (Google Authenticator, Authy, 1Password…),
          then enter the 6-digit code it shows.
        </Typography>
      )}
      {mode === 'verify' && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
          Enter the 6-digit code from your authenticator app.
        </Typography>
      )}

      {mode === 'enroll' && (
        loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : enroll ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2 }}>
              <QRCodeSVG value={enroll.otpauthUri} size={168} includeMargin={false} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
              Can't scan? Enter this key manually:
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
              {enroll.secret}
            </Typography>
            <Divider flexItem sx={{ my: 2 }} />
          </Box>
        ) : null
      )}

      {(mode === 'verify' || (mode === 'enroll' && enroll)) && (
        <form onSubmit={submit}>
          {codeField}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting || code.length !== 6}
            sx={{ mt: 1, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            {submitting ? <CircularProgress size={22} /> : (mode === 'enroll' ? 'Verify & finish setup' : 'Verify')}
          </Button>
        </form>
      )}

      {error && mode === 'enroll' && !enroll && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link component="button" type="button" variant="body2" onClick={onCancel} underline="hover">
          Back to sign in
        </Link>
      </Box>
    </Box>
  );
};

export default OperatorMfaStep;
